import React, { useState, useEffect } from 'react';
import CustomDropdown from '../CustomDropdown/CustomDropdown';
import ColorDropdown from '../ColorDropdown/ColorDropdown';
import CounterInput from '../CounterInput/CounterInput';
import './LineMarkerSettings.scss';
import { LineWeight } from './LineTypeItem';
import { ReactComponent as Triangle } from '../../../images/Triangle.svg';
import { ReactComponent as Diamond } from '../../../images/Diamond.svg';
import { ReactComponent as Asterick } from '../../../images/Asterisk.svg';
import { ReactComponent as CrossMark } from '../../../images/CrossMark.svg';

const LineTypeIcon: React.FC<{ type: string }> = ({ type }) => {
  const [style, thickness] = type.split('-');
  return (
    <div
      className={`line-type-icon ${style === 'dashed' ? 'line-dashed' : 'line-solid'}`}
      style={{ height: `${thickness}px` }}
    />
  );
};

const MarkerTypeIcon: React.FC<{ type: string }> = ({ type }) => {
  return <div className={`marker-type-icon marker-${type}`} />;
};

interface LineMarkerSettingsProps {
  title: string;
  icon: React.ReactNode | string;
  title2: string;
  icon2: React.ReactNode | string;
  onLineAndMarkerChange: (settings: {
    lineType: string;
    lineColor: string;
    lineWeight: number;
    markerType: string;
    markerColor: string;
    markerSize: number;
  }) => void;
  initialSettings: {
    lineType: string;
    lineColor: string;
    lineWeight: number;
    markerType: string;
    markerColor: string;
    markerSize: number;
  };
  onLineReset: () => void;
  onMarkerReset: () => void;
}

const LineMarkerSettings: React.FC<LineMarkerSettingsProps> = ({
  title,
  icon,
  title2,
  icon2,
  onLineAndMarkerChange,
  initialSettings,
  onLineReset,
  onMarkerReset,
}) => {
  const [lineType, setLineType] = useState(initialSettings.lineType);
  const [lineColor, setLineColor] = useState(initialSettings.lineColor);
  const [lineWeight, setLineWeight] = useState(initialSettings.lineWeight);

  const [markerType, setMarkerType] = useState(initialSettings.markerType);
  const [markerColor, setMarkerColor] = useState(initialSettings.markerColor);
  const [markerSize, setMarkerSize] = useState(initialSettings.markerSize);

  useEffect(() => {
    onLineAndMarkerChange({
      lineType,
      lineColor,
      lineWeight,
      markerType,
      markerColor,
      markerSize,
    });
  }, [lineType, lineColor, lineWeight, markerType, markerColor, markerSize]);

  useEffect(() => {
    setLineType(initialSettings.lineType);
    setLineColor(initialSettings.lineColor);
    setLineWeight(initialSettings.lineWeight);
    setMarkerType(initialSettings.markerType);
    setMarkerColor(initialSettings.markerColor);
    setMarkerSize(initialSettings.markerSize);
  }, [initialSettings]);

  const lineTypeOptions = [
    { value: 'solid-3', icon: <LineWeight thickness={3} /> },
    { value: 'solid-2', icon: <LineWeight thickness={2} /> },
    { value: 'solid', icon: <LineWeight thickness={1} /> },
    { value: 'dashed-3', icon: <LineWeight thickness={1} isDashed height={4} /> },
    { value: 'dashed-2', icon: <LineWeight thickness={2} isDashed /> },
    { value: 'dashed-1', icon: <LineWeight height={2} isDashed /> },
  ];

  const colorOptions = [
    { value: '#35BB21' },
    { value: '#3D7FFF' },
    { value: '#0094BD' },
    { value: '#FDB022' },
    { value: '#C471F3' },
    { value: '#E94E89' },
    { value: '#FA6F38' },
  ];

  const markerTypeOptions = [
    { value: 'triangle', icon: <Triangle /> },
    { value: 'diamond', icon: <Diamond /> },
    {
      value: 'circle',
      icon: (
        <div
          style={{
            width: '16px',
            height: '16px',
            borderRadius: '8px',
            background: '#B8C5CC',
          }}
        />
      ),
    },
    {
      value: 'square',
      icon: (
        <div
          style={{
            width: '16px',
            height: '16px',
            background: '#B8C5CC',
          }}
        />
      ),
    },
    { value: 'asterick', icon: <Asterick /> },
    { value: 'croosmark', icon: <CrossMark /> },
  ];

  return (
    <section className='chart-settings'>
      <header className='section-header'>
        <div className='title-container'>
          {typeof icon === 'string' ? <img src={icon} alt={`${title} icon`} className='section-icon' /> : icon}
          <h2 className='section-title'>{title}</h2>
        </div>
        <div className='reset-link' onClick={onLineReset}>
          Reset
        </div>
      </header>

      <div className='settings-content'>
        <CustomDropdown
          label='Type'
          value={lineType}
          onChange={setLineType}
          options={lineTypeOptions}
          icon={<LineTypeIcon type={lineType} />}
        />
        <ColorDropdown label='Color' value={lineColor} onChange={setLineColor} options={colorOptions} />
        <CounterInput label='Weight' value={lineWeight} onChange={setLineWeight} min={1} max={5} />
      </div>

      <div className='section-divider' />

      <header className='section-header'>
        <div className='title-container'>
          {typeof icon2 === 'string' ? <img src={icon2} alt={`${title2} icon`} className='section-icon' /> : icon2}
          <h2 className='section-title'>{title2}</h2>
        </div>
        <div className='reset-link' onClick={onMarkerReset}>
          Reset
        </div>
      </header>

      <div className='settings-content'>
        <CustomDropdown
          label='Type'
          value={markerType}
          onChange={setMarkerType}
          options={markerTypeOptions}
          icon={<MarkerTypeIcon type={markerType} />}
        />
        <ColorDropdown label='Color' value={markerColor} onChange={setMarkerColor} options={colorOptions} />
        <CounterInput label='Size' value={markerSize} onChange={setMarkerSize} min={1} max={5} />
      </div>
    </section>
  );
};

export default LineMarkerSettings;
